import { addDays, datesInclusive, daysBetween, isIsoDate, parseIsoDate } from "./date.ts";
import { isActiveOn } from "./lifecycle.ts";
import {
  DEFAULT_BANDS,
  DomainError,
  type Category,
  type Entry,
  type IsoDate,
  type MatrixDocument,
  type Operation,
  type Outcome,
  type SelectableOutcome,
  type ScoreBand,
  type TrailDefinition,
} from "./model.ts";
import { dueStatus, isRuleEligible, ruleVersionOn } from "./repeat.ts";

export interface OutcomeWeight {
  readonly numerator: number;
  readonly denominator: number;
}

export const OUTCOME_WEIGHTS: Readonly<Record<Outcome, OutcomeWeight>> = Object.freeze({
  T: Object.freeze({ numerator: 1, denominator: 1 }),
  W: Object.freeze({ numerator: 1, denominator: 1 }),
  C: Object.freeze({ numerator: 0, denominator: 1 }),
  M: Object.freeze({ numerator: 0, denominator: 1 }),
  B: Object.freeze({ numerator: 0, denominator: 0 }),
});

export interface OperationDayContribution {
  readonly operationId: string;
  readonly categoryId: string;
  readonly eligible: boolean;
  readonly due: boolean;
  readonly recorded: boolean;
  readonly outcome: Outcome | null;
  /** The complete normal-cell vocabulary excludes the import-only M marker. */
  readonly displayOutcome: SelectableOutcome | null;
  /** Stored manual Byes and computed schedule Byes look identical until inspected. */
  readonly displaySource: "stored" | "generated-schedule" | "required-blank" | "unresolved-blank" | "inactive-blank" | "imported-hidden";
  /** Future cells are persisted and inspectable, but remain projections until their date arrives. */
  readonly projected: boolean;
  readonly numerator: number;
  readonly denominator: number;
  readonly remaining: boolean;
  readonly ruleVersionId: string | null;
  /** Why this operation does or does not participate in the displayed day. */
  readonly applicability: "inactive" | "unscheduled" | "optional" | "due";
  /** A retained outcome on a lifecycle-inactive or rule-ineligible date. */
  readonly recordedOutsideSchedule: boolean;
}

export interface DayScore {
  readonly date: IsoDate;
  readonly projected: boolean;
  readonly numerator: number;
  readonly denominator: number;
  /** Exact points in [0,100], or null when nothing scored. */
  readonly score: number | null;
  readonly bandId: string | null;
  readonly target: number | null;
  readonly targetMet: boolean | null;
  readonly recordedCount: number;
  readonly dueCount: number;
  readonly remainingOperationIds: readonly string[];
  readonly contributions: readonly OperationDayContribution[];
}

export type TrailNullReason = "insufficient-history" | "no-scorable-days" | "no-active-operations";

export interface TrailScore {
  readonly id: string;
  readonly days: number;
  readonly windowStart: IsoDate;
  readonly windowEnd: IsoDate;
  readonly score: number | null;
  readonly scoredDays: number;
  readonly bandId: string | null;
  readonly nullReason: TrailNullReason | null;
}

export interface ScoreOptions {
  readonly categoryId?: string;
  readonly operationIds?: readonly string[];
  /** Trusted present boundary used to label future projections and settle flexible cells. */
  readonly asOf?: IsoDate;
}

interface Index {
  readonly operations: ReadonlyMap<string, Operation>;
  readonly categories: ReadonlyMap<string, Category>;
  readonly entries: ReadonlyMap<string, Outcome>;
}

function entryKey(operationId: string, date: IsoDate): string {
  return `${operationId}\u0000${date}`;
}

function indexDocument(document: MatrixDocument): Index {
  const operations = new Map<string, Operation>();
  const categories = new Map<string, Category>();
  const entries = new Map<string, Outcome>();
  for (const category of document.categories) {
    if (categories.has(category.id)) throw new DomainError("DUPLICATE_CATEGORY", `Duplicate category id ${category.id}`);
    categories.set(category.id, category);
  }
  for (const operation of document.operations) {
    if (operations.has(operation.id)) throw new DomainError("DUPLICATE_OPERATION", `Duplicate operation id ${operation.id}`);
    if (!categories.has(operation.categoryId)) {
      throw new DomainError("UNKNOWN_CATEGORY", `Operation ${operation.id} refers to unknown category ${operation.categoryId}`);
    }
    operations.set(operation.id, operation);
  }
  for (const entry of document.entries) {
    if (!operations.has(entry.operationId)) {
      throw new DomainError("UNKNOWN_OPERATION", `Entry refers to unknown operation ${entry.operationId}`);
    }
    parseIsoDate(entry.date);
    entries.set(entryKey(entry.operationId, entry.date), entry.outcome); // last input mutation wins
  }
  return { operations, categories, entries };
}

function operationActive(index: Index, operation: Operation, date: IsoDate): boolean {
  const category = index.categories.get(operation.categoryId);
  return category !== undefined && isActiveOn(category.activePeriods, date) && isActiveOn(operation.activePeriods, date);
}

function matchingOperations(document: MatrixDocument, options: ScoreOptions): Operation[] {
  let operations = [...document.operations];
  if (options.categoryId !== undefined) operations = operations.filter((operation) => operation.categoryId === options.categoryId);
  if (options.operationIds !== undefined) {
    const selected = new Set(options.operationIds);
    operations = operations.filter((operation) => selected.has(operation.id));
  }
  const order = new Map<string, number>();
  for (const category of document.categories) {
    category.operationOrder.forEach((id, position) => order.set(id, position));
  }
  return operations.sort((left, right) => {
    const categoryOrder = document.categories.findIndex((category) => category.id === left.categoryId) -
      document.categories.findIndex((category) => category.id === right.categoryId);
    if (categoryOrder !== 0) return categoryOrder;
    return (order.get(left.id) ?? Number.MAX_SAFE_INTEGER) - (order.get(right.id) ?? Number.MAX_SAFE_INTEGER);
  });
}

export function classifyScore(score: number | null, bands: readonly ScoreBand[] = DEFAULT_BANDS): string | null {
  if (score === null) return null;
  let selected: string | null = null;
  for (const band of bands) if (band.min <= score) selected = band.id;
  return selected;
}

function entryAt(index: Index, operationId: string, date: IsoDate): Outcome | undefined {
  return index.entries.get(entryKey(operationId, date));
}

function displayedCell(
  outcome: Outcome | undefined,
  applicability: OperationDayContribution["applicability"],
  generatedSchedule: boolean,
): Pick<OperationDayContribution, "displayOutcome" | "displaySource"> {
  if (outcome === "M") return { displayOutcome: null, displaySource: "imported-hidden" };
  if (outcome !== undefined) return { displayOutcome: outcome, displaySource: "stored" };
  if (applicability === "inactive") return { displayOutcome: null, displaySource: "inactive-blank" };
  if (applicability === "due") return { displayOutcome: null, displaySource: "required-blank" };
  return generatedSchedule
    ? { displayOutcome: "B", displaySource: "generated-schedule" }
    : { displayOutcome: null, displaySource: "unresolved-blank" };
}

function scheduledOutcomeAt(index: Index, operation: Operation, date: IsoDate): Outcome | undefined {
  if (!operationActive(index, operation, date)) return undefined;
  const version = ruleVersionOn(operation.ruleVersions, date);
  if (!isRuleEligible(version.rule, date)) return undefined;
  return entryAt(index, operation.id, date);
}

export function scoreDay(document: MatrixDocument, date: IsoDate, options: ScoreOptions = {}): DayScore {
  parseIsoDate(date);
  const asOf = options.asOf ?? date;
  parseIsoDate(asOf);
  const projected = date > asOf;
  const index = indexDocument(document);
  const contributions: OperationDayContribution[] = [];
  let numerator = 0;
  let denominator = 0;
  let recordedCount = 0;
  let dueCount = 0;
  const remainingOperationIds: string[] = [];

  for (const operation of matchingOperations(document, options)) {
    const outcome = entryAt(index, operation.id, date);
    const recorded = outcome !== undefined;
    const active = operationActive(index, operation, date);
    const version = active ? ruleVersionOn(operation.ruleVersions, date) : null;
    const eligible = version !== null && isRuleEligible(version.rule, date);
    if (!active || !eligible || version === null) {
      const applicability = active ? "unscheduled" : "inactive";
      contributions.push({
        operationId: operation.id,
        categoryId: operation.categoryId,
        eligible: false,
        due: false,
        recorded,
        outcome: outcome ?? null,
        ...displayedCell(outcome, applicability, active),
        projected,
        numerator: 0,
        denominator: 0,
        remaining: false,
        ruleVersionId: version?.id ?? null,
        applicability,
        recordedOutsideSchedule: recorded,
      });
      continue;
    }

    const due = dueStatus(version.rule, date, {
      outcomeAt: (candidate) => scheduledOutcomeAt(index, operation, candidate),
    });
    const weight = recorded ? OUTCOME_WEIGHTS[outcome] : due.due ? { numerator: 0, denominator: 1 } : { numerator: 0, denominator: 0 };
    const remaining = !recorded && due.due;
    const applicability = due.due ? "due" : "optional";
    const optionalSettled = version.rule.period.kind === "rolling" || due.quotaRemaining === 0 || date < asOf;
    numerator += weight.numerator;
    denominator += weight.denominator;
    if (recorded) recordedCount += 1;
    if (due.due) dueCount += 1;
    if (remaining) remainingOperationIds.push(operation.id);
    contributions.push({
      operationId: operation.id,
      categoryId: operation.categoryId,
      eligible: true,
      due: due.due,
      recorded,
      outcome: outcome ?? null,
      ...displayedCell(outcome, applicability, optionalSettled),
      projected,
      numerator: weight.numerator,
      denominator: weight.denominator,
      remaining,
      ruleVersionId: version?.id ?? null,
      applicability,
      recordedOutsideSchedule: false,
    });
  }

  const score = denominator === 0 ? null : (numerator / denominator) * 100;
  const target = document.target ?? null;
  return {
    date,
    projected,
    numerator,
    denominator,
    score,
    bandId: classifyScore(score, document.bands),
    target,
    targetMet: score === null || target === null ? null : score >= target,
    recordedCount,
    dueCount,
    remainingOperationIds,
    contributions,
  };
}

/** Public item-name projections must use this predicate rather than looking at
 * the stored outcome alone. Retained out-of-schedule T/W records are private
 * history and do not represent a successful scored item for the day. */
export function isSuccessfulScoredContribution(contribution: OperationDayContribution): boolean {
  return contribution.eligible && !contribution.recordedOutsideSchedule &&
    (contribution.outcome === "T" || contribution.outcome === "W");
}

export function unticked(document: MatrixDocument, date: IsoDate, options: ScoreOptions = {}): readonly string[] {
  return scoreDay(document, date, options).remainingOperationIds;
}

function firstActiveDate(document: MatrixDocument, options: ScoreOptions): IsoDate | null {
  const categories = new Map(document.categories.map((category) => [category.id, category]));
  const starts = matchingOperations(document, options).flatMap((operation) => {
    const category = categories.get(operation.categoryId);
    if (!category) return [];
    return operation.activePeriods.flatMap((operationPeriod) =>
      category.activePeriods.flatMap((categoryPeriod) => {
        const from = operationPeriod.from > categoryPeriod.from ? operationPeriod.from : categoryPeriod.from;
        const ends = [operationPeriod.toExclusive, categoryPeriod.toExclusive]
          .filter((value): value is string => value !== null)
          .sort();
        const toExclusive = ends[0] ?? null;
        return toExclusive !== null && from >= toExclusive ? [] : [from];
      })
    );
  }).filter(isIsoDate).sort();
  return starts[0] ?? null;
}

export function scoreTrail(
  document: MatrixDocument,
  definition: TrailDefinition,
  today: IsoDate,
  options: ScoreOptions = {},
): TrailScore {
  parseIsoDate(today);
  if (!Number.isInteger(definition.days) || definition.days < 1) {
    throw new DomainError("INVALID_TRAIL", "trail days must be a positive integer");
  }
  const windowStart = addDays(today, -definition.days);
  const windowEnd = addDays(today, -1);
  const activeStart = firstActiveDate(document, options);
  const base = { id: definition.id, days: definition.days, windowStart, windowEnd };
  if (activeStart === null) {
    return { ...base, score: null, scoredDays: 0, bandId: null, nullReason: "no-active-operations" };
  }
  if (daysBetween(activeStart, today) < definition.days) {
    return { ...base, score: null, scoredDays: 0, bandId: null, nullReason: "insufficient-history" };
  }
  const index = indexDocument(document);
  const operations = matchingOperations(document, options);
  const windowDates = datesInclusive(windowStart, windowEnd);
  if (!windowDates.some((date) => operations.some((operation) => operationActive(index, operation, date)))) {
    return { ...base, score: null, scoredDays: 0, bandId: null, nullReason: "no-active-operations" };
  }
  const scores = windowDates
    .map((date) => scoreDay(document, date, { ...options, asOf: options.asOf ?? today }).score)
    .filter((score): score is number => score !== null);
  if (scores.length === 0) {
    return { ...base, score: null, scoredDays: 0, bandId: null, nullReason: "no-scorable-days" };
  }
  const score = scores.reduce((sum, value) => sum + value, 0) / scores.length;
  return {
    ...base,
    score,
    scoredDays: scores.length,
    bandId: classifyScore(score, document.bands),
    nullReason: null,
  };
}

export function firstRecordedDate(document: MatrixDocument, through?: IsoDate): IsoDate | null {
  if (through !== undefined) parseIsoDate(through);
  const dates = document.entries.map((entry) => entry.date)
    .filter(isIsoDate)
    .filter((date) => through === undefined || date <= through)
    .sort();
  return dates[0] ?? null;
}

export function validateUniqueEntries(entries: readonly Entry[]): void {
  const seen = new Set<string>();
  for (const entry of entries) {
    const key = entryKey(entry.operationId, entry.date);
    if (seen.has(key)) throw new DomainError("DUPLICATE_ENTRY", `Duplicate entry for ${entry.operationId} on ${entry.date}`);
    seen.add(key);
  }
}
